import React from 'react';
import PropTypes from 'prop-types';
import { startsWith, assign, isEmpty, upperFirst, startCase, isNil, toNumber, every, some } from 'lodash';

import { post, put } from 'helpers/api';
import { requirementShape, FIELDS } from './types';
import { isPresent } from 'helpers/string_helpers';
import { NEW_LOCATION_ID_PREFIX } from './locations_section/locations_constants';

const OFFICE_ASSET_CLASS_ID = 1;
const INDUSTRIAL_ASSET_CLASS_ID = 3;
const LAND_ASSET_CLASS_ID = 5;

export default class FormWrapper extends React.Component {
  static propTypes = {
    createPath: PropTypes.string.isRequired,
    formComponent: PropTypes.elementType.isRequired,
    googleMapsApiKey: PropTypes.string.isRequired,
    investmentTypes: PropTypes.array.isRequired,
    parentPath: PropTypes.string.isRequired,
    propertyTypes: PropTypes.array.isRequired,
    propertyUses: PropTypes.arrayOf(
      PropTypes.shape({ id: PropTypes.number, label: PropTypes.string })
    ).isRequired,
    requirement: requirementShape,
    stateOptions: PropTypes.array.isRequired,
    transactionTypes: PropTypes.array.isRequired
  }

  state = {
    saving: false,
    requirement: this.props.requirement
  }

  assetClassEquivalence(class1, class2) {
    return class1 === class2 || (
      (class1 >= class2 * 100) &&
      (class1 < (class2 + 1) * 100)
    );
  }

  hasAssetClassId = (assetClassIds, assetClassId) => {
    return some(
      assetClassIds,
      selectedAssetClassId => this.assetClassEquivalence(selectedAssetClassId, assetClassId)
    );
  }

  hasOnlyAssetClassId = (assetClassIds, assetClassId) => {
    return every(
      assetClassIds,
      selectedAssetClassId => this.assetClassEquivalence(selectedAssetClassId, assetClassId)
    );
  };

  fieldsToHide = () => {
    let hideFields = [];
    const selectedAssetClassIds = this.state.requirement.assetClassIds;

    // Act as if Office is selected if no selection has been made
    const assetClassIds = selectedAssetClassIds.length === 0 ? [OFFICE_ASSET_CLASS_ID] : selectedAssetClassIds;

    if (this.hasOnlyAssetClassId(assetClassIds, LAND_ASSET_CLASS_ID)) {
      hideFields = hideFields.concat([
        FIELDS.UNITS,
        FIELDS.YEAR_BUILT,
        FIELDS.TENANCY,
        FIELDS.VACANCY_PERCENT,
        FIELDS.CAP_RATE,
        FIELDS.BUILDING_SIZE
      ]);
    }

    if (!this.hasAssetClassId(assetClassIds, INDUSTRIAL_ASSET_CLASS_ID)) {
      hideFields.push(FIELDS.DOCK_HIGH_DOORS);
    }

    if (!this.hasAssetClassId(assetClassIds, OFFICE_ASSET_CLASS_ID)) {
      hideFields.push(FIELDS.BUILDING_CLASS);
    }

    if (!this.hasAssetClassId(assetClassIds, LAND_ASSET_CLASS_ID)) {
      hideFields.push(FIELDS.LOT_SIZE);
    }

    return hideFields;
  }

  sanitizeSaveBody = (requirement) => {
    return {
      ...requirement,
      locations: requirement.locations.map(location => ({
        ...location,
        id: startsWith(location.id, NEW_LOCATION_ID_PREFIX) ? undefined : location.id
      }))
    };
  }

  getErrors() {
    const { parentType, name, transactionType, locations, assetClassIds } = this.state.requirement;
    const errors = [];
    const locationsToSave = locations.filter(location => !location._destroy);

    if (!isPresent(name)) errors.push(`${parentType === 'Profile' ? 'Criteria' : 'Requirement'} Name cannot be empty`);
    if (!isPresent(transactionType)) errors.push('Transaction Type cannot be empty');


    locationsToSave.forEach((location) => {
      if (location.polygonSearch) {
        if (isEmpty(location.polygonGeojson)) errors.push('Drawing cannot be empty');
      } else if (isEmpty(location.state) && isEmpty(location.zip)) {
        errors.push('Location must include state or zip');
      }
    });

    if (parentType == 'Profile') {
      if (assetClassIds.length === 0) {
        errors.push('Must select a property type');
      }

      if (locationsToSave.length === 0) {
        errors.push('Must add at least one location');
      }
    }

    const minMaxFields = ['yearBuilt', 'buildingSize', 'capRate', 'numberOfUnits', 'salePrice'];

    minMaxFields.map(field => upperFirst(field))
      .forEach((field) => {
        const hasValues = !isNil(this.state.requirement[`max${field}`])
          && !isNil(this.state.requirement[`min${field}`]);
        const maxIsLower =
          toNumber(this.state.requirement[`max${field}`]) < toNumber(this.state.requirement[`min${field}`]);

        if (hasValues && maxIsLower) {
          errors.push(`Invalid ${startCase(field)} min/max pair of values`);
        }
      });

    return errors;
  }

  onSave = () => {
    const { createPath, parentPath } = this.props;
    const { requirement } = this.state;
    const { parentId, parentType } = requirement;

    const { locations, ...setParams } = this.sanitizeSaveBody(requirement);
    setParams.locationsAttributes = locations;

    this.setState({ saving: true });

    const body = {
      set: setParams,
      parentId,
      parentType
    };

    const apiPromise = requirement && requirement.updatePath ?
      put(requirement.updatePath, body) : post(createPath, body);

    apiPromise
      .then(() => window.location.assign(parentPath))
      .catch(errors => this.setState({ saving: false, errorMessage: errors[0] }));
  }

  onCancel = () => {
    window.location.assign(this.props.parentPath);
  }

  onChange = (value, key) => {
    this.setState(prevState => ({
      requirement: {
        ...prevState.requirement,
        [key]: value
      }
    }));
  }

  onPropertyTypeChanged = (typeId, checked) => {
    this.setState((prevState) => {
      const { assetClassIds } = prevState.requirement;
      let updatedAssetClassIds = [...(assetClassIds || [])];

      if (checked) {
        updatedAssetClassIds.push(typeId);
      } else {
        updatedAssetClassIds = updatedAssetClassIds.filter(propertyTypeId => propertyTypeId !== typeId);
      }

      const requirement = {
        ...prevState.requirement,
        assetClassIds: updatedAssetClassIds
      };

      if (assetClassIds.includes(5) && !updatedAssetClassIds.includes(5)) {
        assign(requirement, { lotSizeUnits: '', maxLotSize: null, minLotSize: null });
      }

      return { requirement };
    });
  }

  render() {
    const {
      googleMapsApiKey,
      investmentTypes,
      parentPath,
      propertyTypes,
      propertyUses,
      stateOptions,
      transactionTypes
    } = this.props;

    const FormComponent = this.props.formComponent;

    return (
      <div className="requirements-form-container">
        <FormComponent
          errors={this.getErrors()}
          fieldsToHide={this.fieldsToHide()}
          googleMapsApiKey={googleMapsApiKey}
          investmentTypeOptions={investmentTypes}
          parentPath={parentPath}
          propertyTypeOptions={propertyTypes}
          propertyUseOptions={propertyUses}
          requirement={this.state.requirement}
          saving={this.state.saving}
          stateOptions={stateOptions}
          transactionOptions={transactionTypes}
          onCancel={this.onCancel}
          onChange={this.onChange}
          onPropertyTypeChanged={this.onPropertyTypeChanged}
          onSave={this.onSave}
        />
      </div>
    );
  }
}
